// ---------------------------------------------------------------------------
// How the reveal reads a judged answer, and how it sends a ruling back.
//
// Split out of `RevealScreen.tsx` when that file passed two and a half thousand
// lines. Everything here is a question about ONE submission — what was played,
// who said what about it, what it pays, and how to say that in three words — and
// none of it draws anything. That is why it is a `.ts` and not a `.tsx`: four of
// the five files beside it ask these questions, and a shared answer that carried
// markup would drag a component graph behind every import of it.
//
// The funnel described below is the whole point of the file. It was already a
// funnel inside the screen; putting it in a module of its own is what makes it
// one that cannot be worked around by a frame that fancies reading
// `submission.valid` itself.
// ---------------------------------------------------------------------------

import { NUMBERS_SCORING } from '@countdown/shared';
import type { ClientMessage, ClientRound, ClientSubmission } from '@countdown/shared';
import { ruledOutLabel as labelForRuling } from '../../../lib/ruling.js';

// --- Rulings, behind one adapter -------------------------------------------
//
// **Everything this screen knows about how an answer was judged comes out of
// `rulingOf`, and every ruling it sends goes through `overrideMessage` or
// `undoRulingMessage`. That is deliberate and it is the only rule in this
// section.**
//
// The engine was being changed underneath this file while it was written — host
// rulings became opt-in and a ruling became undoable — which is why the reads
// were funnelled through here in the first place. That contract has since
// landed, so what follows is the real thing rather than a guess, and the funnel
// stays: a leaderboard row, an answer frame, a score row and the best-word slide
// all ask the same question and none of them may answer it themselves.
//
// What each field means, from `ClientSubmission`'s own notes:
//
//   `word` / `answer`  the word exactly as it was submitted, present even when
//                      it is not a word and not on the board, so the room can
//                      see what was played (DESIGN §6.8)
//   `judgedValid`      what the DICTIONARY made of it, before any ruling
//   `valid`            the ruling in FORCE, which is `judgedValid` unless a host
//                      ruling overrides it
//   `hostOverride`     whether a host ruling is in force and which way — null
//                      also covers a ruling that was made and then taken back
//
// `hostOverride !== null` and `valid !== judgedValid` are deliberately different
// questions: a host may confirm what the dictionary already said, which is a
// ruling that changed nothing. The row says `host ruled` on the first of those,
// because what the chip reports is that a person decided this, not that the
// score moved.
//
// Rulings are a setting — `settings.hostRulings` — and turning it on means the
// game is not recorded at all. Nothing here may offer a control the room has not
// opted into, which is why `RulingControl` asks.

/** How one answer stands: what was played, who said what, and what it pays. */
export interface Ruling {
  /** The word the player actually tried, whatever anybody ruled about it. */
  word: string;
  /** What the dictionary said. Null until the round has been judged. */
  dictionary: boolean | null;
  /** A host ruling currently in force, and which way. Null when there is none. */
  host: 'allow' | 'reject' | null;
  /** What the round pays on: the host's word where there is one, else the book's. */
  allowed: boolean;
}

export function rulingOf(submission: ClientSubmission): Ruling {
  const host = submission.hostOverride ?? null;
  return {
    word: (submission.word ?? submission.answer ?? '').trim(),
    dictionary: submission.judgedValid ?? null,
    host,
    // The host has the last word in both directions, so the override is read
    // first. `valid` is already the composed answer and the two agree by
    // construction; composing it again here is what keeps the composition in
    // one place rather than trusting two fields to stay in step. An unjudged
    // answer is not a rejected one.
    allowed: host === null ? submission.valid !== false : host === 'allow',
  };
}

/** The host ruling one way or the other on somebody's word. */
export function overrideMessage(playerId: string, allow: boolean): ClientMessage {
  return { type: 'override-word', playerId, ruling: allow ? 'allow' : 'reject' };
}

/**
 * Taking a ruling back off a word.
 *
 * `ruling: null` is the undo, and the engine treats it as an event the room
 * watches rather than a silent revert: the score moved when the ruling landed
 * and it moves again when it goes, so the table is owed the reason both times.
 * Nothing here has to arrange that — the same broadcast carries it.
 */
export function undoRulingMessage(playerId: string): ClientMessage {
  return { type: 'override-word', playerId, ruling: null };
}

/** Was this answer ruled out, by the book or by the host? */
export function rejected(submission: ClientSubmission): boolean {
  return !rulingOf(submission).allowed;
}

export function answerTextOf(submission: ClientSubmission): string {
  return rulingOf(submission).word;
}

/**
 * Whether this player took the risk, as opposed to sitting the round out.
 *
 * An empty answer used to be read as "did not buzz", and it is not the same
 * thing at all. Somebody who buzzed, froze and let the ten seconds go has done
 * the one brave thing the round asks for, and telling them they never buzzed is
 * the screen calling them a coward for the thing they were already punished
 * for. A failed buzz locks the player out and clears `buzzedBy`, so the record
 * of it is `lockedOut`; a solved one is `solvedBy`; `buzzedBy` covers the buzz
 * that was still open when the round ended.
 *
 * Every one of the three is a fact the server sends. Nothing here is inferred
 * from the answer being empty, which is exactly the inference that was wrong.
 */
export function buzzedIn(round: ClientRound, playerId: string): boolean {
  return (
    round.solvedBy === playerId ||
    round.buzzedBy === playerId ||
    (round.lockedOut ?? []).includes(playerId)
  );
}

/** The nine letters, or an empty board when this is not a letters round. */
export function letterTilesOf(round: ClientRound): string[] {
  return (round.tiles ?? []).map((tile) => tile.letter);
}

/**
 * Why a word did not stand, in three words at most.
 *
 * Two different failures and a player is owed the difference: a word spelled
 * from the board but absent from the dictionary is a near miss, and a word using
 * letters that were never there is an AI opponent hallucinating, which only it
 * can do — the engine refuses an unbuildable word from everybody else. DESIGN
 * 6.8.
 *
 * **The classification is `lib/ruling.ts` and this is only the wording.** The
 * game record page makes the same claim about the same word, and two screens
 * working it out separately and disagreeing is precisely what that file exists
 * to stop. This screen used to say `no such word` where the record says `not a
 * word`; one game does not get two names for one ruling, and the shorter one
 * wins — it is a chip in a tight row, and it is structurally parallel with `not
 * on the board`, which is the distinction a player actually has to spot.
 *
 * `hostRuled` is a parameter rather than read off the ruling, because the two
 * callers want different things from it. The panel draws the host's chip beside
 * this one on purpose — a ruling that CONFIRMED a rejection is two facts, and
 * `host ruled` alone would leave the room wondering what the word did wrong — so
 * it asks for the dictionary's reason only. The standing has one line and folds
 * both into it.
 */
export function ruledOutLabel(round: ClientRound, ruling: Ruling, hostRuled = false): string {
  return labelForRuling({
    type: round.type,
    word: ruling.word,
    tiles: letterTilesOf(round),
    hostRuled,
  });
}

/**
 * Which scoring band an answer landed in, in the words the reveal says out loud.
 *
 * **Derived from `NUMBERS_SCORING` rather than restating it, which is what the
 * loop is for.** This used to carry its own `distance === 0`, `<= 5`, `<= 10`,
 * and those are the same three numbers `scoreNumbersValue` in the engine pays
 * out of — one implementation the game scores from, one copy the screen labels
 * from, agreeing today and connected by nothing. Moving a band in
 * `constants.ts` would have the reveal call a scoring answer `too far out` and
 * draw it as not good while the engine handed the player points for it, and
 * nothing anywhere would have failed. Same defect class as the `TILE_FLIP_MS`
 * drift in CLAUDE.md, without the test that catches that one.
 *
 * The wording is still this screen's own and the table decides nothing about
 * it: `exact` for the zero band, `within n` for the rest, and `too far out`
 * when no band takes the distance — which is exactly the case
 * `scoreNumbersValue` returns 0 for, because it walks the same list the same
 * way. Do not put the three numbers back.
 */
export function numbersBand(value: number, target: number): string {
  const distance = Math.abs(value - target);
  for (const band of NUMBERS_SCORING) {
    if (distance > band.withinInclusive) continue;
    return band.withinInclusive === 0 ? 'exact' : `within ${band.withinInclusive}`;
  }
  return 'too far out';
}

/**
 * The line under a name in the score rows: what they scored, and why.
 *
 * `raced` is whether the conundrum had a buzzer — `settings.conundrumBuzzer`,
 * through `conundrumIsRaced`. It is a parameter rather than something read off
 * the round because this file takes one submission and no game state, which is
 * what lets the record page share it. Defaulted to the show's own rule, so every
 * caller that has never heard of the setting keeps the wording it had.
 *
 * With the buzzer off there is no such thing as a buzz, so `never buzzed` would
 * be the standing reporting on a rule the room switched off. Anybody may answer
 * inside the clock and everybody correct scores, which makes an absent
 * submission the ordinary `nothing submitted` it is on every other round type.
 */
export function reasonForSubmission(
  round: ClientRound,
  submission: ClientSubmission | undefined,
  raced = true,
): string {
  const buzzers = round.type === 'conundrum' && raced;

  if (submission === undefined) {
    // No submission at all in a conundrum means no buzz: every buzz that ends,
    // however it ends, writes one.
    return buzzers ? 'never buzzed' : 'nothing submitted';
  }

  switch (round.type) {
    case 'letters': {
      // The same vocabulary the panel's chips use, so the standing and the row
      // it recaps read as one claim rather than two about the same word.
      //
      // **A good word is the word and nothing else, and the `, n letters` that
      // used to follow it was costing a name.** At the row size a sixteen-player
      // standing runs at, `ScoreRow` puts the name and this line side by side
      // and shares the shortfall between them in proportion to what each asked
      // for — so twenty characters of recap here is a display name truncated to
      // four. The count was already the third telling: the word is spelled out
      // beside it and the score is 44px to the right.
      const ruling = rulingOf(submission);
      const word = ruling.word.toUpperCase();
      if (word.length === 0) return 'nothing submitted';
      if (ruling.host === null && ruling.allowed) return word;
      // The host branch is the helper's now rather than a case here: a ruling
      // is a person deciding, and it is in force over whatever the book said,
      // so naming the dictionary's reason under one names the wrong reason.
      return `${word}, ${ruledOutLabel(round, ruling, ruling.host !== null)}`;
    }
    case 'numbers': {
      const value = submission.value ?? null;
      if (value === null) return 'nothing submitted';
      return `${value}, ${numbersBand(value, round.target ?? 0)}`;
    }
    case 'conundrum': {
      const answer = answerTextOf(submission);
      if (answer.length === 0) {
        // Buzzed with nothing typed when the ten seconds ran out. Anything that
        // was typed is submitted and judged, so an empty answer here is a
        // player who never got a word down rather than one whose word was lost.
        if (!buzzers) return 'nothing submitted';
        return buzzedIn(round, submission.playerId) ? 'buzzed, no word in time' : 'never buzzed';
      }
      if (rejected(submission)) return `${answer.toUpperCase()} was wrong`;
      return `${answer.toUpperCase()}, solved it`;
    }
  }
}
